import React, { Suspense, useContext, useState } from 'react';
import { AsyncOptionsSection } from '../components/Options/AsyncOptionsSection';
import { Header } from '../components/Header';
import { InvoiceTextTemplatesOptions } from '../components/Options/InvoiceTextTemplatesOptions';
import { FleetWaybillSettingsOptions } from '../components/Options/FleetWaybillSettingsOptions';
import { Navigation } from '../components/Navigation';
import { SideBar } from '../components/SideBar';
import { SideBarContext } from '../context/SideBarContext';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import {
    useCreateDeviceGroup as createDeviceGroup,
    useCreateEssencePriceGroup as createEssencePriceGroup,
    useCreateReedInstallationGroup,
    useCreateConsumableServiceGroup,
    useDeleteDeviceGroup as deleteDeviceGroup,
    useDeleteEssencePriceGroup as deleteEssencePriceGroup,
    useDeleteReedInstallationGroup,
    useDeleteConsumableServiceGroup,
    useGetAllConfigurations,
    useGetConfigColors,
    useGetDeviceGroups as getDeviceGroups,
    useGetEssencePriceGroups as getEssencePriceGroups,
    useGetReedInstallationGroups,
    useGetConsumableServiceGroups,
    useUpdateConfigurations,
    useUpdateDeviceGroup as updateDeviceGroup,
    useUpdateEssencePriceGroup as updateEssencePriceGroup,
    useUpdateReedInstallationGroup,
    useUpdateConsumableServiceGroup,
} from '../utils/configuration-hooks';
import { AllConfigurations, Configuration, ConsumableServiceGroup, DeviceGroup, EssencePriceGroup, ReedInstallationGroup } from '../models/configuration-models';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColorSelect } from '../components/ColorSelect';
import CommonInputText from '../components/CommonInputText';
import { sortAlphabetically } from '../utils/helper-functions';
import { OptionsContext, OptionsProvider } from '../context/OptionsContext';
import { toast } from "react-toastify";
import { CustomToastContent } from "../components/CustomToastContent";
import { toastOptionsTopRight } from "../utils/Enums/ToastOptions";

export const Options = () => {
    const { sideBarVissible } = useContext(SideBarContext);

    return (
        <OptionsProvider>
            <div className={`d-flex wrapper ${sideBarVissible && 'sidebar-active'} w-100`}>
                <SideBar>
                    <Navigation />
                </SideBar>
                <div className='d-flex content-wrapper flex-column justify-content-start align-items-center w-100'>
                    <Header title='Options' />
                    <Suspense fallback={<Spinner animation='border' className='mt-3' />}>
                        <OptionsInner />
                        <div className='d-flex justify-content-center p-4 w-100'>
                            <div className='w-80p'>
                                <InvoiceTextTemplatesOptions />
                            </div>
                        </div>
                        <FleetWaybillSettingsOptions />
                    </Suspense>
                </div>
            </div>
        </OptionsProvider>
    );
};

const OptionsInner = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { control, register, formState, handleSubmit, setValue } = useForm<AllConfigurations>();
    const { allConfigurations } = useContext(OptionsContext);

    const useSubmitHandler = async (formValue: AllConfigurations) => {
        const prefixes = formValue.prefixes;
        const result: Configuration[] = Object.entries({ ...formValue, prefixes: [] }).flatMap(([, value]) => value);
        await useUpdateConfigurations(result, prefixes);
        resourceCache(useGetAllConfigurations).clear();
        resourceCache(useGetConfigColors).clear();
        toast.success(<CustomToastContent content={[t('configUpdated')]} />, toastOptionsTopRight);
    };
    const miscConfigurations = {
        maxLoginAttempts: allConfigurations.misc?.maxLoginAttempts ?? '10',
    };
    const bonusConfigurations = {
        salesRate: allConfigurations.misc?.salesRate ?? '0',
    };

    return (
        <Form
            id='form'
            className='d-flex flex-column align-items-start p-4  w-100'
            noValidate
            onSubmit={handleSubmit(useSubmitHandler)}
        >
            <Form.Group className='d-flex flex-column align-items-start  mb-2 align-items-center w-100'>
                <h4 className='mb-4 ps-2'>{t('companyInfo')}</h4>
                {Object.entries(allConfigurations.aromaInfo).map(([name, configuration], index) => {
                    setValue(`aromaInfo.${index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between align-items-center w-80p' key={index}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                <CommonInputText
                                    defaultValue={configuration}
                                    labelTextColor='text-black'
                                    type='text'
                                    fieldError={formState.errors.aromaInfo?.[index]?.value}
                                    hookRegister={register(`aromaInfo.${index}.value`, {
                                        required: { value: true, message: t(name) },
                                    })}
                                />
                            </div>
                        </div>
                    );
                })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start  mb-2 align-items-center w-100'>
                <h4 className='mb-4'>{t('bankInfo')}</h4>
                {Object.entries(allConfigurations.bankInfo).map(([name, configuration], index) => {
                    setValue(`bankInfo.${index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between align-items-center w-80p' key={index}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                <CommonInputText
                                    defaultValue={configuration}
                                    labelTextColor='text-black'
                                    type='text'
                                    fieldError={formState.errors.bankInfo?.[index]?.value}
                                    hookRegister={register(`bankInfo.${index}.value`, {
                                        required: { value: true, message: t(name) },
                                    })}
                                />
                            </div>
                        </div>
                    );
                })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start  mb-2 align-items-center w-100'>
                <h4 className='mb-4'>{t('colors')}</h4>
                {Object.entries(allConfigurations.colors).map(([name, configuration], index) => {
                    setValue(`colors.${index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between align-items-center w-80p' key={index}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                <Controller
                                    control={control}
                                    name={`colors.${index}.value`}
                                    rules={{ required: true }}
                                    defaultValue={configuration}
                                    render={({ field }) => (
                                        <ColorSelect colorValue={field.value.toString()} updateValue={field.onChange} />
                                    )}
                                />
                            </div>
                        </div>
                    );
                })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start  mb-2 align-items-center w-100'>
                <h4 className='mb-4'>{t('countdownDays')}</h4>
                {Object.entries(allConfigurations.timerDays).map(([name, value], index) => {
                    setValue(`timerDays.${index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between align-items-center w-80p' key={index}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                <CommonInputText
                                    defaultValue={value}
                                    labelTextColor='text-black'
                                    type='number'
                                    fieldError={formState.errors.timerDays?.[index]?.value}
                                    hookRegister={register(`timerDays.${index}.value`, {
                                        required: { value: true, message: t(name) },
                                    })}
                                />
                            </div>
                        </div>
                    );
                })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start  mb-2 align-items-center w-100'>
                <h4 className='mb-4'>{t('workTimes')}</h4>
                {Object.entries(allConfigurations.workTimes).map(([name, value], index) => {
                    setValue(`workTimes.${index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between align-items-center w-80p' key={index}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                <CommonInputText
                                    defaultValue={value}
                                    labelTextColor='text-black'
                                    type='text'
                                    fieldError={formState.errors.workTimes?.[index]?.value}
                                    hookRegister={register(`workTimes.${index}.value`, {
                                        required: { value: true, message: t(name) },
                                    })}
                                />
                            </div>
                        </div>
                    );
                })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start  mb-2 align-items-center w-100'>
                <h4 className='mb-4'>{t('invoiceDetails')}</h4>
                {Object.entries(allConfigurations.invoiceDetails).map(([name, value], index) => {
                    setValue(`invoiceDetails.${index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between align-items-center w-80p' key={index}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                <CommonInputText
                                    disabled={name === 'invoiceCurrentNumber' || name === 'creditNoteCurrentNumber'}
                                    defaultValue={value}
                                    labelTextColor='text-black'
                                    type='text'
                                    fieldError={formState.errors.invoiceDetails?.[index]?.value}
                                    hookRegister={register(`invoiceDetails.${index}.value`, {
                                        required: { value: !(name === 'invoiceCurrentNumber' || name === 'creditNoteCurrentNumber'), message: t(name) },
                                    })}
                                />
                            </div>
                        </div>
                    );
                })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start  mb-2 align-items-center w-100'>
                <h4 className='mb-4'>{t('VATprefixes')}</h4>
                {allConfigurations.prefixes
                    .sort((a, b) => sortAlphabetically(a.mainGroup, b.mainGroup))
                    .map((value, index) => {
                        return (
                            <div className='d-flex mb-2 justify-content-between align-items-center w-80p' key={index}>
                                <h6>{value.mainGroup}</h6>
                                <div className='w-50'>
                                    <Controller
                                        control={control}
                                        defaultValue={value}
                                        render={({ field }) => (
                                            <Form.Control
                                                defaultValue={value.prefix}
                                                type='text'
                                                onChange={({ target }) =>
                                                    field.onChange({
                                                        mainGroup: value.mainGroup,
                                                        prefix: target.value,
                                                    })
                                                }
                                            />
                                        )}
                                        name={`prefixes.${index}`}
                                        rules={{ required: { value: true, message: value.mainGroup } }}
                                    />
                                </div>
                            </div>
                        );
                    })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start  mb-5 align-items-center w-100'>
                <h4 className='mb-4'>{t('misc')}</h4>
                {Object.entries(miscConfigurations).map(([name, value], index) => {
                    setValue(`misc.${index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between w-80p' key={index}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                <CommonInputText
                                    defaultValue={value}
                                    labelTextColor='text-black'
                                    type='number'
                                    fieldError={formState.errors.misc?.[index]?.value}
                                    hookRegister={register(`misc.${index}.value`, {
                                        required: { value: true, message: t(name) },
                                    })}
                                />
                            </div>
                        </div>
                    );
                })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start  mb-5 align-items-center w-100'>
                <h4 className='mb-4'>{t('emailInfo')}</h4>
                {Object.entries(allConfigurations.emailInfo).map(([name, value], index) => {
                    setValue(`emailInfo.${index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between w-80p' key={index}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                {name === 'emailBody' ? (
                                    <div className='p-0 d-flex flex-column w-100'>
                                        <Form.Control
                                            as='textarea'
                                            rows={6}
                                            className={`focus:border-wsotMain-500 w-100 ${formState.errors.emailInfo?.[index]?.value
                                                    ? 'border-red-500 focus:border-red-500'
                                                    : ''
                                                }`}
                                            defaultValue={value}
                                            {...register(`emailInfo.${index}.value`, {
                                                required: { value: true, message: t(name) },
                                            })}
                                        />
                                        {formState.errors.emailInfo?.[index]?.value && (
                                            <p className='input-error'>
                                                *{formState.errors.emailInfo?.[index]?.value?.message}
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <CommonInputText
                                        defaultValue={value}
                                        labelTextColor='text-black'
                                        type='text'
                                        fieldError={formState.errors.emailInfo?.[index]?.value}
                                        hookRegister={register(`emailInfo.${index}.value`, {
                                            required: { value: name !== 'emailCopy', message: t(name) },
                                        })}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </Form.Group>
            <Form.Group className='d-flex flex-column align-items-start mb-5 align-items-center w-100'>
                <h4 className='mb-4'>{t('bonuses')}</h4>
                {Object.entries(bonusConfigurations).map(([name, value], index) => {
                    setValue(`misc.${Object.keys(miscConfigurations).length + index}.name`, name);
                    return (
                        <div className='d-flex mb-2 justify-content-between align-items-center w-80p' key={name}>
                            <h6>{t(name)}</h6>
                            <div className='w-50'>
                                <CommonInputText
                                    defaultValue={value}
                                    labelTextColor='text-black'
                                    type='number'
                                    fieldError={formState.errors.misc?.[Object.keys(miscConfigurations).length + index]?.value}
                                    hookRegister={register(`misc.${Object.keys(miscConfigurations).length + index}.value`, {
                                        required: { value: true, message: t(name) },
                                    })}
                                />
                            </div>
                        </div>
                    );
                })}
                <AsyncOptionsSection load={getEssencePriceGroups}>
                    <EssencePriceGroupsOptions />
                </AsyncOptionsSection>
                <AsyncOptionsSection load={getDeviceGroups}>
                    <DeviceGroupsOptions />
                </AsyncOptionsSection>
                <AsyncOptionsSection load={useGetReedInstallationGroups}>
                    <BonusGroupsOptions<ReedInstallationGroup>
                        title={t('reedInstallationGroups')}
                        load={useGetReedInstallationGroups}
                        create={useCreateReedInstallationGroup}
                        update={useUpdateReedInstallationGroup}
                        remove={useDeleteReedInstallationGroup}
                    />
                </AsyncOptionsSection>
                <AsyncOptionsSection load={useGetConsumableServiceGroups}>
                    <BonusGroupsOptions<ConsumableServiceGroup>
                        title={t('consumableServiceGroups')}
                        load={useGetConsumableServiceGroups}
                        create={useCreateConsumableServiceGroup}
                        update={useUpdateConsumableServiceGroup}
                        remove={useDeleteConsumableServiceGroup}
                    />
                </AsyncOptionsSection>
            </Form.Group>
            <div className='d-flex w-100 justify-content-evenly'>
                <Button className='w-25' type='submit' form='form'>
                    {t('save')}
                </Button>
                <Button variant='outline-primary color-main' className='w-25' type='reset' form='form'>
                    {t('reset')}
                </Button>
            </div>
        </Form>
    );
};

const emptyPriceGroup: EssencePriceGroup = {
    groupNumber: 1,
    color: '#6FBCFF',
    bonusRate: 0,
};

const emptyDeviceGroup: DeviceGroup = {
    groupNumber: 1,
    color: '#6FBCFF',
    bonusRate: 0,
};

const priceGroupPanelStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    border: '1px solid #e5e9f0',
    borderRadius: 10,
    boxShadow: '0 10px 24px rgba(33, 37, 41, 0.06)',
    overflow: 'hidden',
    width: '50%',
    alignSelf: 'flex-end',
};

const priceGroupPreviewStyle = (color: string): React.CSSProperties => ({
    backgroundColor: color,
    borderRadius: 6,
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.35)',
    color: '#fff',
    minHeight: 46,
});

const EssencePriceGroupsOptions = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { allConfigurations } = useContext(OptionsContext);
    const currencyConfiguration = allConfigurations.invoiceDetails?.currency ?? '€';
    const [readPriceGroups, refreshPriceGroups] = useAsyncResource(getEssencePriceGroups, []);
    const priceGroups = readPriceGroups();
    const [newPriceGroup, setNewPriceGroup] = useState<EssencePriceGroup>(emptyPriceGroup);
    const [showAddPriceGroupModal, setShowAddPriceGroupModal] = useState(false);
    const [priceGroupToEdit, setPriceGroupToEdit] = useState<EssencePriceGroup | null>(null);

    const renderPriceGroupFormRow = (
        priceGroup: EssencePriceGroup,
        onChange: (value: Partial<EssencePriceGroup>) => void,
        isNew = false
    ) => (
        <div
            className='d-grid align-items-end gap-3 p-3'
            style={{
                backgroundColor: isNew ? '#f8fbff' : '#fff',
                borderTop: '1px solid #edf0f4',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            }}
        >
            <Form.Group className='mb-0'>
                <Form.Label className='small fw-bold text-muted mb-1'>{t('group')}</Form.Label>
                <Form.Control
                    type='number'
                    min={1}
                    value={priceGroup.groupNumber}
                    onChange={({ target }) => onChange({ groupNumber: Number(target.value) })}
                />
            </Form.Group>
            <Form.Group className='mb-0'>
                <div className='d-flex align-items-center gap-2'>
                    <div
                        className='d-flex align-items-center justify-content-center px-3 fw-bold'
                        style={priceGroupPreviewStyle(priceGroup.color)}
                    >
                        {t('group')} {priceGroup.groupNumber}
                    </div>
                    <div className='flex-grow-1'>
                        <ColorSelect colorValue={priceGroup.color} updateValue={(color) => color && onChange({ color })} />
                    </div>
                </div>
            </Form.Group>
            <Form.Group className='mb-0'>
                <Form.Label className='small fw-bold text-muted mb-1'>{currencyConfiguration}</Form.Label>
                <Form.Control
                    type='number'
                    step='0.01'
                    min={0}
                    value={priceGroup.bonusRate}
                    onChange={({ target }) => onChange({ bonusRate: Number(target.value) })}
                />
            </Form.Group>
        </div>
    );
    const renderPriceGroupSummaryRow = (priceGroup: EssencePriceGroup) => (
        <tr key={priceGroup.id ?? priceGroup.groupNumber}>
            <td className='ps-4'>
                <div className='d-flex align-items-center gap-3'>
                    <div
                        className='d-flex align-items-center justify-content-center px-3 fw-bold'
                        style={priceGroupPreviewStyle(priceGroup.color)}
                    >
                        {t('group')} {priceGroup.groupNumber}
                    </div>
                </div>
            </td>
            <td className='fw-bold text-end pe-4'>{priceGroup.bonusRate.toFixed(2)} {currencyConfiguration}</td>
            <td className='text-end pe-4'>
                <Button
                    type='button'
                    variant='outline-primary'
                    size='sm'
                    className='d-inline-flex align-items-center justify-content-center p-0'
                    style={{ height: 40, width: 40 }}
                    onClick={() => setPriceGroupToEdit({ ...priceGroup })}
                    title={t('edit')}
                >
                    <FontAwesomeIcon icon={faPencilAlt} className='fa-fw' />
                </Button>
            </td>
        </tr>
    );

    const loadPriceGroups = () => {
        resourceCache(getEssencePriceGroups).clear();
        refreshPriceGroups();
    };

    const savePriceGroup = async (priceGroup: EssencePriceGroup) => {
        await updateEssencePriceGroup(priceGroup);
        setPriceGroupToEdit(null);
        loadPriceGroups();
        toast.success(<CustomToastContent content={[t('configUpdated')]} />, toastOptionsTopRight);
    };

    const createPriceGroup = async () => {
        await createEssencePriceGroup(newPriceGroup);
        setNewPriceGroup(emptyPriceGroup);
        setShowAddPriceGroupModal(false);
        loadPriceGroups();
        toast.success(<CustomToastContent content={[t('configUpdated')]} />, toastOptionsTopRight);
    };

    const deletePriceGroup = async (id?: number) => {
        if (!id) return;
        await deleteEssencePriceGroup(id);
        setPriceGroupToEdit(null);
        loadPriceGroups();
        toast.success(<CustomToastContent content={[t('configUpdated')]} />, toastOptionsTopRight);
    };

    const closeAddPriceGroupModal = () => {
        setNewPriceGroup(emptyPriceGroup);
        setShowAddPriceGroupModal(false);
    };

    return (
        <Form.Group className='d-flex align-items-start mb-2 w-80p'>
            <div className='d-flex justify-content-between align-items-center w-50 mb-2'>
                <div>
                    <h6 className='mb-0 fw-semibold text-dark'>{t('essencePriceGroups')}</h6>
                    <div className='small text-muted'>
                        {priceGroups.length} {t('groups')}
                    </div>
                </div>
            </div>
            <div className='overflow-hidden' style={priceGroupPanelStyle}>
                <div className='table-responsive'>
                    <table className='table table-hover align-middle mb-0'>
                        <thead>
                            <tr>
                                <th scope='col' className='ps-4'>{t('group')}</th>
                                <th scope='col' className='text-end pe-4'>{currencyConfiguration}</th>
                                <th scope='col' className='text-end pe-4'>
                                    <div className='d-flex justify-content-end'>
                                        <Button type='button' size='sm' onClick={() => setShowAddPriceGroupModal(true)}>
                                            {t('add')}
                                        </Button>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {priceGroups.map((priceGroup, index) => (
                                <React.Fragment key={priceGroup.id ?? index}>
                                    {renderPriceGroupSummaryRow(priceGroup)}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <Modal show={showAddPriceGroupModal} onHide={closeAddPriceGroupModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('essencePriceGroups')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className='p-0'>
                    {renderPriceGroupFormRow(
                        newPriceGroup,
                        (value) => setNewPriceGroup({ ...newPriceGroup, ...value }),
                        true
                    )}
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button type='button' size='sm' variant='outline-secondary' onClick={closeAddPriceGroupModal}>
                        {t('close')}
                    </Button>
                    <Button type='button' size='sm' onClick={createPriceGroup}>
                        {t('add')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={!!priceGroupToEdit} onHide={() => setPriceGroupToEdit(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('edit')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className='p-0'>
                    {priceGroupToEdit &&
                        renderPriceGroupFormRow(priceGroupToEdit, (value) =>
                            setPriceGroupToEdit({ ...priceGroupToEdit, ...value })
                        )}
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button
                        type='button'
                        size='sm'
                        variant='outline-danger'
                        onClick={() => deletePriceGroup(priceGroupToEdit?.id)}
                    >
                        {t('delete')}
                    </Button>
                    <div className='d-flex gap-2'>
                        <Button type='button' size='sm' variant='outline-secondary' onClick={() => setPriceGroupToEdit(null)}>
                            {t('close')}
                        </Button>
                        <Button
                            type='button'
                            size='sm'
                            variant='success'
                            disabled={!priceGroupToEdit}
                            onClick={() => priceGroupToEdit && savePriceGroup(priceGroupToEdit)}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </Form.Group>
    );
};

const panelStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    border: '1px solid #e5e9f0',
    borderRadius: 10,
    boxShadow: '0 10px 24px rgba(33, 37, 41, 0.06)',
    overflow: 'hidden',
    width: '50%',
    alignSelf: 'flex-end',
};

const DeviceGroupsOptions = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { allConfigurations } = useContext(OptionsContext);
    const currencyConfiguration = allConfigurations.invoiceDetails?.currency ?? '€';
    const [readDeviceGroups, refreshDeviceGroups] = useAsyncResource(getDeviceGroups, []);
    const deviceGroups = readDeviceGroups();
    const [newDeviceGroup, setNewDeviceGroup] = useState<DeviceGroup>(emptyDeviceGroup);
    const [showAddDeviceGroupModal, setShowAddDeviceGroupModal] = useState(false);
    const [deviceGroupToEdit, setDeviceGroupToEdit] = useState<DeviceGroup | null>(null);

    const renderDeviceGroupFormRow = (
        deviceGroup: DeviceGroup,
        onChange: (value: Partial<DeviceGroup>) => void,
        isNew = false
    ) => (
        <div
            className='d-grid align-items-end gap-3 p-3'
            style={{
                backgroundColor: isNew ? '#f8fbff' : '#fff',
                borderTop: '1px solid #edf0f4',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            }}
        >
            <Form.Group className='mb-0'>
                <Form.Label className='small fw-bold text-muted mb-1'>{t('group')}</Form.Label>
                <Form.Control
                    type='number'
                    min={1}
                    value={deviceGroup.groupNumber}
                    onChange={({ target }) => onChange({ groupNumber: Number(target.value) })}
                />
            </Form.Group>
            <Form.Group className='mb-0'>
                <div className='d-flex align-items-center gap-2'>
                    <div
                        className='d-flex align-items-center justify-content-center px-3 fw-bold'
                        style={priceGroupPreviewStyle(deviceGroup.color)}
                    >
                        {t('group')} {deviceGroup.groupNumber}
                    </div>
                    <div className='flex-grow-1'>
                        <ColorSelect colorValue={deviceGroup.color} updateValue={(color) => color && onChange({ color })} />
                    </div>
                </div>
            </Form.Group>
            <Form.Group className='mb-0'>
                <Form.Label className='small fw-bold text-muted mb-1'>{currencyConfiguration}</Form.Label>
                <Form.Control
                    type='number'
                    step='0.01'
                    min={0}
                    value={deviceGroup.bonusRate}
                    onChange={({ target }) => onChange({ bonusRate: Number(target.value) })}
                />
            </Form.Group>
        </div>
    );
    const renderDeviceGroupSummaryRow = (deviceGroup: DeviceGroup) => (
        <tr key={deviceGroup.id ?? deviceGroup.groupNumber}>
            <td className='ps-4'>
                <div className='d-flex align-items-center gap-3'>
                    <div
                        className='d-flex align-items-center justify-content-center px-3 fw-bold'
                        style={priceGroupPreviewStyle(deviceGroup.color)}
                    >
                        {t('group')} {deviceGroup.groupNumber}
                    </div>
                </div>
            </td>
            <td className='fw-bold text-end pe-4'>{Number(deviceGroup.bonusRate).toFixed(2)} {currencyConfiguration}</td>
            <td className='text-end pe-4'>
                <Button
                    type='button'
                    variant='outline-primary'
                    size='sm'
                    className='d-inline-flex align-items-center justify-content-center p-0'
                    style={{ height: 40, width: 40 }}
                    onClick={() => setDeviceGroupToEdit({ ...deviceGroup })}
                    title={t('edit')}
                >
                    <FontAwesomeIcon icon={faPencilAlt} className='fa-fw' />
                </Button>
            </td>
        </tr>
    );

    const loadDeviceGroups = () => {
        resourceCache(getDeviceGroups).clear();
        refreshDeviceGroups();
    };

    const saveDeviceGroup = async (deviceGroup: DeviceGroup) => {
        await updateDeviceGroup(deviceGroup);
        setDeviceGroupToEdit(null);
        loadDeviceGroups();
        toast.success(<CustomToastContent content={[t('configUpdated')]} />, toastOptionsTopRight);
    };

    const createNewDeviceGroup = async () => {
        await createDeviceGroup(newDeviceGroup);
        setNewDeviceGroup(emptyDeviceGroup);
        setShowAddDeviceGroupModal(false);
        loadDeviceGroups();
        toast.success(<CustomToastContent content={[t('configUpdated')]} />, toastOptionsTopRight);
    };

    const removeDeviceGroup = async (id?: number) => {
        if (!id) return;
        await deleteDeviceGroup(id);
        setDeviceGroupToEdit(null);
        loadDeviceGroups();
        toast.success(<CustomToastContent content={[t('configUpdated')]} />, toastOptionsTopRight);
    };

    const closeAddDeviceGroupModal = () => {
        setNewDeviceGroup(emptyDeviceGroup);
        setShowAddDeviceGroupModal(false);
    };

    return (
        <Form.Group className='d-flex align-items-start mb-2 w-80p'>
            <div className='d-flex justify-content-between align-items-center w-50 mb-2'>
                <div>
                    <h6 className='mb-0 fw-semibold text-dark'>{t('deviceGroups')}</h6>
                    <div className='small text-muted'>
                        {deviceGroups.length} {t('groups')}
                    </div>
                </div>
            </div>
            <div className='overflow-hidden' style={panelStyle}>
                <div className='table-responsive'>
                    <table className='table table-hover align-middle mb-0'>
                        <thead>
                            <tr>
                                <th scope='col' className='ps-4'>{t('group')}</th>
                                <th scope='col' className='text-end pe-4'>{currencyConfiguration}</th>
                                <th scope='col' className='text-end pe-4'>
                                    <div className='d-flex justify-content-end'>
                                        <Button type='button' size='sm' onClick={() => setShowAddDeviceGroupModal(true)}>
                                            {t('add')}
                                        </Button>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {deviceGroups.map((deviceGroup, index) => (
                                <React.Fragment key={deviceGroup.id ?? index}>
                                    {renderDeviceGroupSummaryRow(deviceGroup)}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <Modal show={showAddDeviceGroupModal} onHide={closeAddDeviceGroupModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('deviceGroups')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className='p-0'>
                    {renderDeviceGroupFormRow(
                        newDeviceGroup,
                        (value) => setNewDeviceGroup({ ...newDeviceGroup, ...value }),
                        true
                    )}
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button type='button' size='sm' variant='outline-secondary' onClick={closeAddDeviceGroupModal}>
                        {t('close')}
                    </Button>
                    <Button type='button' size='sm' onClick={createNewDeviceGroup}>
                        {t('add')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={!!deviceGroupToEdit} onHide={() => setDeviceGroupToEdit(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('edit')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className='p-0'>
                    {deviceGroupToEdit &&
                        renderDeviceGroupFormRow(deviceGroupToEdit, (value) =>
                            setDeviceGroupToEdit({ ...deviceGroupToEdit, ...value })
                        )}
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button
                        type='button'
                        size='sm'
                        variant='outline-danger'
                        onClick={() => removeDeviceGroup(deviceGroupToEdit?.id)}
                    >
                        {t('delete')}
                    </Button>
                    <div className='d-flex gap-2'>
                        <Button type='button' size='sm' variant='outline-secondary' onClick={() => setDeviceGroupToEdit(null)}>
                            {t('close')}
                        </Button>
                        <Button
                            type='button'
                            size='sm'
                            variant='success'
                            disabled={!deviceGroupToEdit}
                            onClick={() => deviceGroupToEdit && saveDeviceGroup(deviceGroupToEdit)}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </Form.Group>
    );
};

type BonusGroup = { id?: number; groupNumber: number; color: string; bonusRate: number };

const BonusGroupsOptions = <T extends BonusGroup>({
    title, load, create, update, remove,
}: {
    title: string;
    load: () => Promise<T[]>;
    create: (group: T) => Promise<T>;
    update: (group: T) => Promise<T>;
    remove: (id: number) => Promise<void>;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { allConfigurations } = useContext(OptionsContext);
    const currency = allConfigurations.invoiceDetails?.currency ?? '€';
    const empty = { groupNumber: 1, color: '#6FBCFF', bonusRate: 0 } as T;
    const [readGroups, refreshGroups] = useAsyncResource(load, []);
    const groups = readGroups();
    const [newGroup, setNewGroup] = useState<T>(empty);
    const [editing, setEditing] = useState<T | null>(null);
    const [adding, setAdding] = useState(false);

    const reload = () => {
        resourceCache(load).clear();
        refreshGroups();
    };

    const form = (group: T, change: (value: Partial<T>) => void) => (
        <div className='d-grid align-items-end gap-3 p-3' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <Form.Group><Form.Label>{t('group')}</Form.Label><Form.Control type='number' min={1} value={group.groupNumber}
                onChange={({ target }) => change({ groupNumber: Number(target.value) } as Partial<T>)} /></Form.Group>
            <Form.Group><Form.Label>{t('color')}</Form.Label><ColorSelect colorValue={group.color}
                updateValue={(color) => color && change({ color } as Partial<T>)} /></Form.Group>
            <Form.Group><Form.Label>{currency}</Form.Label><Form.Control type='number' step='0.01' min={0} value={group.bonusRate}
                onChange={({ target }) => change({ bonusRate: Number(target.value) } as Partial<T>)} /></Form.Group>
        </div>
    );

    const notify = () => toast.success(<CustomToastContent content={[t('configUpdated')]} />, toastOptionsTopRight);
    const saveNew = async () => { await create(newGroup); setNewGroup(empty); setAdding(false); reload(); notify(); };
    const saveEdit = async () => { if (!editing) return; await update(editing); setEditing(null); reload(); notify(); };
    const deleteEdit = async () => { if (!editing?.id) return; await remove(editing.id); setEditing(null); reload(); notify(); };

    return (
        <Form.Group className='d-flex align-items-start mb-2 w-80p'>
            <div className='w-50'><h6 className='mb-0 fw-semibold text-dark'>{title}</h6><div className='small text-muted'>{groups.length} {t('groups')}</div></div>
            <div className='overflow-hidden w-50' style={{ border: '1px solid #e5e9f0', borderRadius: 10 }}>
                <table className='table table-hover align-middle mb-0'><thead><tr>
                    <th className='ps-4'>{t('group')}</th><th className='text-end'>{currency}</th>
                    <th className='text-end pe-4'><Button type='button' size='sm' onClick={() => setAdding(true)}>{t('add')}</Button></th>
                </tr></thead><tbody>{groups.map((group) => <tr key={group.id ?? group.groupNumber}>
                    <td className='ps-4'><span className='d-inline-block me-2' style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: group.color }} />{t('group')} {group.groupNumber}</td>
                    <td className='text-end'>{group.bonusRate.toFixed(2)} {currency}</td>
                    <td className='text-end pe-4'><Button type='button' variant='outline-primary' size='sm' onClick={() => setEditing({ ...group })}><FontAwesomeIcon icon={faPencilAlt} /></Button></td>
                </tr>)}</tbody></table>
            </div>
            <Modal show={adding} onHide={() => setAdding(false)} centered><Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
                <Modal.Body className='p-0'>{form(newGroup, (value) => setNewGroup({ ...newGroup, ...value }))}</Modal.Body>
                <Modal.Footer><Button type='button' variant='outline-secondary' onClick={() => setAdding(false)}>{t('close')}</Button><Button type='button' onClick={saveNew}>{t('add')}</Button></Modal.Footer>
            </Modal>
            <Modal show={!!editing} onHide={() => setEditing(null)} centered><Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
                <Modal.Body className='p-0'>{editing && form(editing, (value) => setEditing({ ...editing, ...value }))}</Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'><Button type='button' variant='outline-danger' onClick={deleteEdit}>{t('delete')}</Button>
                    <div className='d-flex gap-2'><Button type='button' variant='outline-secondary' onClick={() => setEditing(null)}>{t('close')}</Button><Button type='button' variant='success' onClick={saveEdit}>{t('save')}</Button></div>
                </Modal.Footer>
            </Modal>
        </Form.Group>
    );
};
